import { isAfter, isWithinInterval } from '@if/core/date'
import { ECreditApplicationStatus, EDecision, EFinancingProgram, EPaymentPlan } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { Action, ActionList, Banner, Caption, ControlledCheckbox, useCan, useDialog, useFormDialog } from '@if/mui'
import { Box } from '@mui/material'
import { access } from '@src/access'
import GetCreditContentConsentText from '@src/containers/CreditApplicationPage/components/creditApplicationForm-selectors'
import { FinancingConfigDto } from '@src/data/api/configs-api/financing-config-api'
import { useQualify } from '@src/data/api/credit-api/credit-api'
import { useApplicablePromoPlanLowestRate, useResolvedPaymentPlan } from '@src/data/api/merchants-api/merchants-api'
import {
  anyDocsAwaitingApproval,
  getDisplayedMaxAmountFinanced,
  mustShowInterestRate,
} from '@src/data/creditapp-selectors'
import { getSelectedPlan } from '@src/data/worksheet-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { Calculator, Send, UserPlus } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { hasPendingAsyncChecks, hasPendingCreditChecks } from '../../credit-hooks'
import { ECreditApplicationStatusMessage, GetStatusFromDecision } from '../../viewCreditApp-selectors'
import ApplicationError from '../applicationError'
import CreditApplicantBanners from '../CreditSection/CreditApplicantBanners'
import PaymentPlanSimulator from '../CreditSection/PaymentPlanSimulator'
import StageContent, { StageField } from './StageContent'

interface Props {
  creditApp: CreditApplication
  merchant: Merchant
  financingConfig: FinancingConfigDto
  canEdit: boolean
}

const consentSchema = yup.object({
  consent: yup.boolean().oneOf([true]).required(),
})

const creditStatus = (creditApp: CreditApplication) => {
  // Pending checks count as "analysing" here, so the step doesn't report an error while its
  // own banner says the checks are still running.
  return GetStatusFromDecision(
    creditApp.finalCreditDecision.decision,
    false,
    creditApp,
    hasPendingCreditChecks(creditApp),
    anyDocsAwaitingApproval(creditApp.requiredDocuments),
  )
}

// Credit descriptor for StageContent: granted amounts + per-applicant banners + actions.
const CreditStageContent = ({ creditApp, merchant, financingConfig, canEdit }: Props) => {
  const { t } = useTranslation()
  const showRate = useCan(access.showRate)
  const [qualify] = useQualify()
  const simulatorDialog = useDialog({
    title: t('viewCreditApplication.paymentPlanSimulator'),
    maxWidth: 'md',
    fullWidth: true,
    hideConfirm: true,
    cancelLabel: t('common.close'),
    content: (ctx) => <PaymentPlanSimulator creditApp={creditApp} merchant={merchant} onApplied={ctx.close} />,
  })
  const consentDialog = useFormDialog<{ consent: boolean }, void, void>({
    title: t('submission.submission'),
    confirmLabel: t('submission.submit'),
    schema: consentSchema,
    defaultValues: { consent: false },
    successMessage: false,
    content: (ctx) => (
      <>
        <GetCreditContentConsentText financingProgram={financingProgramId} />
        <ControlledCheckbox control={ctx.form.control} name="consent" label={t('submission.agreeToTerms')} />
      </>
    ),
    onSubmit: async () => {
      await qualify({
        creditApplicationId: id,
        financingProgramId,
        prequalPromoCode: creditApp.prequalPromoCode ?? undefined,
      })
    },
  })

  const { finalCreditDecision, worksheet, financingProgramId, id, coApplicant, consentHardHit } = creditApp
  const decision = finalCreditDecision.decision
  const config = FinancingProgramConfigs[financingProgramId]
  // Any check still owes a result (banner) vs. this step's numbers aren't computed yet (skeleton).
  const pendingChecks = hasPendingAsyncChecks(creditApp)
  const status = creditStatus(creditApp)
  const mustDisplayInterests = mustShowInterestRate(creditApp, merchant, showRate)

  const hasActivePaymentPlans = useMemo(() => {
    if (!merchant?.paymentPlans) return false
    const now = new Date()
    const distantFuture = new Date()
    distantFuture.setFullYear(distantFuture.getFullYear() + 100)
    return merchant.paymentPlans.some((plan) => {
      const from = new Date(plan.activeFrom)
      const to = new Date(plan?.expiresOn ?? distantFuture)
      return isWithinInterval(now, { start: from, end: to }) && isAfter(to, now)
    })
  }, [merchant?.paymentPlans])

  const selectedPlan = useMemo(() => {
    if (creditApp.merchantPaymentPlanId === null) return undefined
    const planId = (worksheet?.paymentPlanId as EPaymentPlan) ?? creditApp.merchantPaymentPlanId
    return getSelectedPlan(merchant, planId)
  }, [creditApp.merchantPaymentPlanId, worksheet?.paymentPlanId, merchant])

  const appliedPaymentPlanId = worksheet?.paymentPlanId ?? creditApp.merchantPaymentPlanId ?? undefined
  const [resolvedPlan] = useResolvedPaymentPlan(merchant?.id, appliedPaymentPlanId)
  const isSingleTermPlan = resolvedPlan?.termRates.length === 1

  const isCustomBuydownActive = useCan(access.customBuydownActive, { merchant })
  const [lowestBuydownTermRate, isFetchingLowestBuydownRate] = useApplicablePromoPlanLowestRate(
    isCustomBuydownActive ? merchant?.id : undefined,
  )
  const isComputingCredit =
    (consentHardHit && creditApp.normsCreditDecision == null) || (isCustomBuydownActive && isFetchingLowestBuydownRate)

  const promoRate = selectedPlan?.promoCode != null ? selectedPlan.reducedInterestRate : null
  const interestRate = promoRate ?? worksheet?.promotionReducedInterestRate ?? finalCreditDecision.interestRate
  const interestRateLabel =
    selectedPlan?.promoCode != null
      ? `${t('viewCreditApplication.interestRate')} (${t('common.promotion')})`
      : worksheet?.promotionReducedInterestRate != null
        ? t('viewCreditApplication.promotionalInterestRate')
        : t('viewCreditApplication.interestRate')

  // Granted amounts — shown once the credit is approved (each row gated by config).
  const fields: StageField[] =
    decision !== EDecision.Approved
      ? []
      : [
          ...(mustDisplayInterests && !resolvedPlan && !isCustomBuydownActive
            ? [{ label: interestRateLabel, value: `${interestRate}%` }]
            : []),
          ...(config.useMaxPayment
            ? [
                {
                  label: t('viewCreditApplication.maxMonthlyPayment'),
                  value: formatCurrency(finalCreditDecision.maxPmtAmount),
                },
              ]
            : []),
          ...(config.displayAmountGrantedCreditTile
            ? [
                {
                  label: t('viewCreditApplication.amountGranted'),
                  value: formatCurrency(
                    getDisplayedMaxAmountFinanced(
                      financingProgramId,
                      finalCreditDecision.maxAmountFinanced,
                      financingConfig,
                    ),
                  ),
                },
              ]
            : []),
          {
            label: t('viewCreditApplication.maxTerm'),
            value: creditApp.isSinglePayment
              ? t('viewCreditApplication.isSinglePayment')
              : `${finalCreditDecision.maxTermDuration} ${t('common.months')}`,
          },
          // Selected promo plan: code + reduced-rate duration. Its rate rows carry the same
          // merchant gate as the interest-rate field above — which is suppressed on a resolved
          // plan, so they'd otherwise be the one place a hidden rate still showed.
          ...(isCustomBuydownActive
            ? mustDisplayInterests
              ? [
                  {
                    label: t('viewCreditApplication.interestRate'),
                    value: `${finalCreditDecision.interestRate}%`,
                  },
                  ...(lowestBuydownTermRate != null
                    ? [
                        {
                          label: `${t('viewCreditApplication.interestRate')} (${t('common.promoPlan')})`,
                          value: (
                            <>
                              {`${lowestBuydownTermRate.promoRate * 100}% (${lowestBuydownTermRate.promoDurationMonths ?? lowestBuydownTermRate.termMonths} ${t('common.months')})*`}
                              <Caption sx={{ display: 'block' }}>
                                {t('viewCreditApplication.longerTermFinancingNote')}
                              </Caption>
                            </>
                          ),
                        },
                      ]
                    : []),
                ]
              : []
            : resolvedPlan != null
            ? [
                { label: t('fundingSummary.promotion'), value: resolvedPlan.label ?? '' },
                ...(mustDisplayInterests
                  ? [
                      {
                        label: `${t('viewCreditApplication.interestRate')} (${t('common.promotion')})`,
                        value: `${
                          (isSingleTermPlan
                            ? resolvedPlan.termRates[0].promoRate
                            : Math.min(...resolvedPlan.termRates.map((tr) => tr.promoRate))) * 100
                        }%`,
                      },
                      {
                        label: `${t('viewCreditApplication.interestRate')} (${t('common.regular')})`,
                        value: `${finalCreditDecision.interestRate}%`,
                      },
                    ]
                  : []),
                ...(isSingleTermPlan
                  ? [
                      {
                        label: t('viewCreditApplication.promotionalTerm'),
                        value: `${resolvedPlan.termRates[0].promoDurationMonths ?? resolvedPlan.termRates[0].termMonths} ${t('common.months')}`,
                      },
                    ]
                  : []),
              ]
            : selectedPlan != null
              ? [
                  { label: t('fundingSummary.promotion'), value: selectedPlan.promoCode },
                  ...(mustDisplayInterests
                    ? [
                        {
                          label: `${t('viewCreditApplication.interestRate')} (${t('common.regular')})`,
                          value: `${finalCreditDecision.interestRate}%`,
                        },
                      ]
                    : []),
                  {
                    label: t('viewCreditApplication.promotionalTerm'),
                    value: `${selectedPlan.reducedRateDurationInMonths} ${t('common.months')}`,
                  },
                ]
              : []),
        ]

  // App-level "previous application" warning (Products gets the conditional-approval variant).
  const previousAppWarningKey =
    config.requiresCreditStepIntervention && creditApp.applicant.creditAlerts?.hasPreviousApplication
      ? financingProgramId === EFinancingProgram.Products &&
        creditApp.normsCreditDecision?.decision === EDecision.Approved
        ? ('viewCreditApplication.previousAppConditionalApproval' as const)
        : ('viewCreditApplication.previousApp' as const)
      : undefined

  // Per-applicant OK / alert banners + the state's message and interactive tools.
  const renderCustomContent = () => {
    // Read-only app still awaiting the client's worksheet/approval (declines fall through to the per-applicant banners).
    if (
      creditApp.worksheet === null &&
      creditApp.consentHardHit === true &&
      !canEdit &&
      config.worksheetIsNeededForCredit &&
      decision !== EDecision.Refused
    ) {
      return <Banner severity="info">{t('viewCreditApplication.awaitingClientApproval')}</Banner>
    }
    if (status === ECreditApplicationStatusMessage.Error) return <ApplicationError />
    if (!consentHardHit && creditApp.status === ECreditApplicationStatus.Active) {
      return canEdit ? (
        <ActionList actions={[qualifyAction]} variant="button" menuLabel={t('viewCreditApplication.actions')} />
      ) : (
        <Banner severity="info">{t('viewCreditApplication.awaitingClientConsent')}</Banner>
      )
    }
    return (
      <>
        {previousAppWarningKey && <Banner severity="warning">{t(previousAppWarningKey)}</Banner>}
        <CreditApplicantBanners
          applicant={creditApp.applicant}
          coApplicant={coApplicant}
          status={status}
          showAlerts={config.requiresCreditStepIntervention}
        />
        {decision === EDecision.Approved && hasActivePaymentPlans && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ActionList actions={[openSimulatorAction]} variant="icon" menuLabel={t('viewCreditApplication.actions')} />
          </Box>
        )}
      </>
    )
  }

  const changeCoapp: Action = {
    icon: UserPlus,
    label: coApplicant ? t('viewCreditApplication.changeCoapp') : t('viewCreditApplication.addCoapp'),
    href: `/credits/${financingProgramId}/${id}/edit?addCoApp=true#coapplicant`,
    disabled: finalCreditDecision.isHardDecline,
  }
  const qualifyAction: Action = {
    icon: Send,
    label: t('viewCreditApplication.qualify'),
    onClick: () => void consentDialog.create(),
  }
  const openSimulatorAction: Action = {
    icon: Calculator,
    label: t('viewCreditApplication.paymentPlanSimulator'),
    onClick: () => void simulatorDialog.open(),
  }

  const buildActions = (): Action[] => {
    if (!canEdit) return []
    if (status === ECreditApplicationStatusMessage.Declined && config.allowCoappAfterDecline) return [changeCoapp]
    return []
  }

  const actions = buildActions()

  return (
    <StageContent
      fields={fields}
      columns={3}
      loading={isComputingCredit}
      pendingChecks={pendingChecks}
      customContent={renderCustomContent()}
      actions={
        actions.length > 0 ? (
          <ActionList actions={actions} variant="button" menuLabel={t('viewCreditApplication.actions')} />
        ) : undefined
      }
    />
  )
}

export default CreditStageContent
